"use client";

import { useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Binance REST depth snapshot — free, no key
// https://api.binance.com/api/v3/depth?symbol=BTCUSDT&limit=5000
// ---------------------------------------------------------------------------

export interface OrderBookLevel {
  price: number;
  quantity: number;
  notional: number; // price * qty (USD liquidity)
}

export interface OrderBookSnapshot {
  bids: OrderBookLevel[];
  asks: OrderBookLevel[];
  midPrice: number;
  totalBidLiquidity: number;
  totalAskLiquidity: number;
}

const SYMBOL_MAP: Record<string, string> = {
  BTC: "BTCUSDT",
  ETH: "ETHUSDT",
  BNB: "BNBUSDT",
  SOL: "SOLUSDT",
  XRP: "XRPUSDT",
  ADA: "ADAUSDT",
  DOGE: "DOGEUSDT",
  DOT: "DOTUSDT",
  AVAX: "AVAXUSDT",
  MATIC: "MATICUSDT",
  LINK: "LINKUSDT",
  UNI: "UNIUSDT",
  ATOM: "ATOMUSDT",
  LTC: "LTCUSDT",
  NEAR: "NEARUSDT",
  AAVE: "AAVEUSDT",
};

function mapSymbol(symbol: string): string {
  const upper = symbol.toUpperCase();
  if (SYMBOL_MAP[upper]) return SYMBOL_MAP[upper]!;
  if (upper.endsWith("USDT")) return upper;
  return `${upper}USDT`;
}

export function useBinanceOrderBook(
  symbol: string,
  limit = 1000,
  refreshMs = 10000,
) {
  const [snapshot, setSnapshot] = useState<OrderBookSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const pair = mapSymbol(symbol);

    async function fetchDepth() {
      try {
        const res = await fetch(
          `https://api.binance.com/api/v3/depth?symbol=${pair}&limit=${limit}`,
        );
        if (!res.ok) {
          if (!cancelled) {
            setError("not_available");
            setLoading(false);
          }
          return;
        }
        const data = (await res.json()) as {
          bids: [string, string][];
          asks: [string, string][];
        };
        if (cancelled) return;

        const bids: OrderBookLevel[] = data.bids.map(([p, q]) => {
          const price = parseFloat(p);
          const quantity = parseFloat(q);
          return { price, quantity, notional: price * quantity };
        });
        const asks: OrderBookLevel[] = data.asks.map(([p, q]) => {
          const price = parseFloat(p);
          const quantity = parseFloat(q);
          return { price, quantity, notional: price * quantity };
        });

        const bestBid = bids[0]?.price ?? 0;
        const bestAsk = asks[0]?.price ?? 0;
        const midPrice = (bestBid + bestAsk) / 2;
        const totalBidLiquidity = bids.reduce((a, b) => a + b.notional, 0);
        const totalAskLiquidity = asks.reduce((a, b) => a + b.notional, 0);

        setSnapshot({
          bids,
          asks,
          midPrice,
          totalBidLiquidity,
          totalAskLiquidity,
        });
        setError(null);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "fetch_failed");
          setLoading(false);
        }
      }
    }

    void fetchDepth();
    const id = window.setInterval(fetchDepth, refreshMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [symbol, limit, refreshMs]);

  return { snapshot, loading, error };
}
